/** LGU 테넌트 ID */
export const LGU_TENANT_ID = 1;

/**
 * GSR 테넌트 ID
 * tenantId가 2인 경우 GSR 계정
 */
export const GSR_TENANT_ID = 2;

/** GS MY SHOP 테넌트 ID */
export const GS_MY_SHOP_TENANT_ID = 5;

/** Hyundai E&C 테넌트 ID */
export const HYUNDAI_ENC_TENANT_ID = 9;

/** JTBC 테넌트 ID */
export const JTBC_TENANT_ID = 10;

/** Yeongcheon 테넌트 ID */
export const YEONGCHEON_TENANT_ID = 11;

/** KBC 테넌트 ID */
export const KBC_TENANT_ID = 3;

/** CHOSUN 테넌트 ID */
export const CHOSUN_TENANT_ID = 4;

/** 와글 미디어 테넌트 ID */
export const WAGGLE_MEDIA_TENANT_ID = 6;

/** ECK EDU 테넌트 ID */
export const ECK_EDU_TENANT_ID = 7;

/** 불혹청년 테넌트 ID */
export const BULHOK_CHEONGNYEON_TENANT_ID = 8;

/** FN_KOREA 테넌트 ID */
export const FN_KOREA_TENANT_ID = 12;

/** 비상교육 테넌트 ID */
export const VISANG_TENANT_ID = 14;

/** 삼성생명 테넌트 ID */
export const SAMSUNG_LIFE_TENANT_ID = 15;

/**
 * 이 Account `id`는 테넌트별 LTS 제한을 무시하고, LTS 관련 UI만 기본 테넌트(미등록/0)와 동일하게 쓴다.
 * `0`이면 비활성화. 실제 허용할 계정 id로 변경한다.
 *
 * `: number`를 붙여야 값이 리터럴로 추론되지 않아 `=== 0` 비활성화 분기와 타입이 맞는다.
 */
export const LTS_DEFAULT_UI_OVERRIDE_ACCOUNT_ID: number = 14744;

/**
 * LTS UI를 기본 테넌트 설정으로 덮어쓸지 여부.
 * @see LTS_DEFAULT_UI_OVERRIDE_ACCOUNT_ID
 */
export function isLtsDefaultUiOverrideAccount(
  accountId: number | undefined | null,
): boolean {
  if (LTS_DEFAULT_UI_OVERRIDE_ACCOUNT_ID === 0) return false;
  return accountId != null && accountId === LTS_DEFAULT_UI_OVERRIDE_ACCOUNT_ID;
}

/** LTS 모델 선택값 — UI의 추론(사고) 모델은 `advanced`, 심층 검색 기반 모델은 `multimodal` */
export type LtsLockedModelId = 'light' | 'advanced' | 'multimodal';

/** LTS 분석 모드 전체 어휘 (BE LtsAnalysisMode name·serve ltsFeatureConfig.supportedModes 와 동일) */
export type LtsAnalysisModeId =
  | 'light'
  | 'advanced'
  | 'portrait'
  | 'multimodal'
  | 'midform';

export const LTS_ANALYSIS_MODE_IDS: LtsAnalysisModeId[] = [
  'light',
  'advanced',
  'portrait',
  'multimodal',
  'midform',
];

// ---------------------------------------------------------------------------
// Sidebar Logo Config — 테넌트별 사이드바 로고 설정
// ---------------------------------------------------------------------------

/**
 * 사이드바 로고 오버라이드 설정.
 * TenantUiConfig.sidebarLogo가 null이면 기본 waka 로고.
 * 새 테넌트 로고 추가 시: /public/logos/에 파일 넣고 여기 경로만 설정.
 */
export interface SidebarLogoOverride {
  src: string;
  alt: string;
  /** 로고 하단 캡션(예: 'Powered by WakaShorts'). 미설정이면 캡션 미노출. */
  caption?: string;
}

// ---------------------------------------------------------------------------
// Tenant UI Config — 테넌트별 UI 렌더링 설정
// ---------------------------------------------------------------------------

/**
 * 테넌트별 UI 렌더링 설정.
 * 값은 space UI 프로파일 serve 로부터 재구성된다(useTenantUiConfig / buildTenantUiConfigFromProfile).
 */
export interface TenantUiConfig {
  /** 커스텀 홈 화면 사용 */
  useCustomHome: boolean;
  redirectHome: boolean;
  /** 홈 가이드 섹션 노출 */
  showHomeGuide: boolean;
  /** LTS(하이라이트 만들기) 버튼 노출 */
  showLtsButton: boolean;
  /** MTS(대본으로 만들기) 버튼 노출 */
  showMtsButton: boolean;
  /** GTS 칩 노출 */
  showGtsChip: boolean;
  /** STC(소재 만들기) 칩 노출 */
  showStcChip: boolean;
  /** 중복 프로젝트 다이얼로그 GSR 문구 사용 여부 */
  duplicateDialogIsGsr: boolean;
  /** LTS 버튼 i18n 키 */
  ltsButtonI18nKey: string;
  /** LTS 모드 선택 스텝(03) 노출 */
  showLtsModeStep: boolean;
  /** LTS 멀티모달 모드 노출 */
  showLtsMultimodalMode: boolean;
  /** LTS 기본 indexLevel */
  ltsDefaultIndexLevel: number;
  /** LTS 업로드 제한 안내 박스 노출 */
  showLtsUploadLimitInfo: boolean;
  /**
   * 단일 모드만 허용할 때 설정. 스텝 03·모드 카드 UI를 숨기고 해당 모델로 고정한다.
   * null이면 사용자가 선택.
   */
  ltsLockedModelId: LtsAnalysisModeId | null;
  /** 인물 포커싱(portrait) 모드 노출 여부 */
  showPortraitMode: boolean;
  /**
   * 모드 선택 초기값. ltsLockedModelId와 달리 잠금이 아니라 기본 선택값.
   * 유저가 다른 모드로 전환 가능. null이면 기존 동작(multimodal).
   */
  ltsDefaultModelId: LtsAnalysisModeId | null;
  /**
   * LTS 분석 필터 섹션(제외 멘트 토글·쇼핑호스트 선택) 노출.
   * voice 카테고리 테넌트(GS 홈쇼핑) 전용 — 선택값은 프로젝트 생성 시 analysisOptions로 전달.
   */
  showLtsAnalysisFilters: boolean;
  /**
   * 노출 허용 LTS 분석 모드 목록 — serve ltsFeatureConfig(프로필 기본값+space override 합성)가
   * 있을 때만 채워진다. null = 구버전 서버 폴백(개별 show* 플래그 + 미드폼 tenant 게이트 사용).
   */
  ltsSupportedModes: LtsAnalysisModeId[] | null;
  /**
   * 커머스 서버 파이프라인 활성(envelope·재필터) — 재필터 패널/메뉴 게이트.
   * showLtsAnalysisFilters(화면 필터 섹션 노출)와 별개 축. serve 부재(구버전 서버) 시
   * 기존 게이트였던 showLtsAnalysisFilters 값을 따른다.
   */
  commerceEnabled: boolean;
  /**
   * 정적 화면 필터 사용 시 추론(advanced) 모드 강제 승격 허용.
   * false 거나 advanced 가 ltsSupportedModes 에 없으면 정적 화면 필터 옵션 자체를 숨긴다
   * (승격 없는 정적 화면 필터는 계약이 깨지고, 미지원 모드 강제는 서버 게이트에 걸린다).
   */
  forceAdvancedOnStaticFilter: boolean;
  /**
   * LTS 자막 번역 노출 — 생성 화면의 "자막 번역" 섹션 게이트.
   * 코드 기본값 false 이며 serve ltsFeatureConfig.captionTranslationEnabled 로만 켜진다.
   */
  showLtsCaptionTranslation: boolean;

  /** 내보내기 화면에서 30fps / 29.97fps 선택을 허용하는 Space 기능 프로필 게이트. */
  exportFpsSelectionEnabled: boolean;

  // ── LTS/기능 제한 ──
  /** LTS만 보여줄지 여부 (홈·사이드바에서 다른 카테고리 숨김) */
  showOnlyLts: boolean;
  /** 이미지 에디터 노출 */
  showImageEditor: boolean;
  /** 홈 화면 서브 카테고리 행(이미지 에디터/브랜드 템플릿/인사이트) 노출 */
  showHomeSubCategories: boolean;
  /** 파일 업로드 시 적용할 indexLevel */
  uploadIndexLevel: number;

  // ── 가시성(Visibility) 플래그 ──
  /** 코인 잔액 표시 (사이드바, 탑바) */
  showCoin: boolean;
  /** 충전 버튼 */
  showCoinCharge: boolean;
  /** 플랜 업그레이드/보러가기 버튼 */
  showPlanUpgrade: boolean;
  /** 프로필 메뉴의 내 플랜·결제 내역 */
  showPaymentMenu: boolean;
  /** 코인 부족/플랜 제한 알림 모달 */
  showPaymentAlert: boolean;

  // ── 계정 ──
  /** 사용자 이메일 표시 (사이드바 계정 영역·계정 드롭다운·프로필 페이지) */
  showEmail: boolean;
  /**
   * 계정 드롭다운의 "스페이스 관리" 메뉴 노출. owner/admin 역할 조건과 AND.
   * 운영자(operator)는 이 플래그와 무관하게 항상 노출(단 owner/admin 이어야 함).
   * 구 tenantId !== 0 게이트를 대체하는 space 단위 플래그.
   */
  showSpaceManagement: boolean;
  /**
   * 계정 셀프서비스 묶음(SSO 스페이스용). off 시 비밀번호 변경·이메일 수신 동의·
   * 고객센터(공지/문의)·회원 탈퇴·고객 지원 채팅 위젯을 일괄 숨김.
   */
  showAccountSelfService: boolean;

  // ── 사이드바 로고 ──
  /** 사이드바 상단 로고 오버라이드. null이면 기본 waka 로고. */
  sidebarLogo: SidebarLogoOverride | null;
  /** Beta 라벨 노출 여부 */
  showBetaLabel: boolean;
  /** AI Video Wizard V2 (인포 영상 만들기) 메뉴 노출. 운영자는 이 플래그와 무관하게 항상 노출. */
  showInfoVideoWizard: boolean;
  /** Broadcast Vault(아카이브로 영상 만들기) 사이드바 메뉴 노출. 운영자는 이 플래그와 무관하게 항상 노출. */
  showBroadcastVaultButton: boolean;

  // ── GS(홈쇼핑) ──
  /**
   * GS 전용 모드. GS 홈쇼핑 방송-제작 전용 동작을 일괄 활성화한다:
   * 에디터 내보내기 프리셋·세이프존·렌더 설정, 출력 스토리 방송 정렬, 데스크톱 렌더 서버 탭.
   * 구 isGsrAccount(tenantId === GSR_TENANT_ID) 하드코딩을 대체하는 space 단위 플래그.
   */
  gsExclusive: boolean;
}

export const DEFAULT_TENANT_UI_CONFIG: TenantUiConfig = {
  useCustomHome: false,
  redirectHome: true,
  showHomeGuide: true,
  showLtsButton: true,
  showMtsButton: true,
  showGtsChip: false,
  showStcChip: false,
  duplicateDialogIsGsr: false,
  ltsButtonI18nKey: 'MySpace.create-lts-button',
  showOnlyLts: false,
  showImageEditor: true,
  showHomeSubCategories: true,
  uploadIndexLevel: 30,
  showLtsModeStep: true,
  showLtsMultimodalMode: true,
  ltsDefaultIndexLevel: 30,
  showLtsUploadLimitInfo: true,
  ltsLockedModelId: null,
  showPortraitMode: true,
  ltsDefaultModelId: null,
  showLtsAnalysisFilters: false,
  ltsSupportedModes: null,
  commerceEnabled: false,
  forceAdvancedOnStaticFilter: true,
  showLtsCaptionTranslation: false,
  exportFpsSelectionEnabled: false,
  showCoin: true,
  showCoinCharge: true,
  showPlanUpgrade: true,
  showPaymentMenu: true,
  showPaymentAlert: true,
  showEmail: true,
  showSpaceManagement: false,
  showAccountSelfService: true,
  sidebarLogo: null,
  showBetaLabel: true,
  showInfoVideoWizard: false,
  showBroadcastVaultButton: false,
  gsExclusive: false,
} as const;
